import {
  CheckCircle2,
  Clock,
  AlertCircle,
  CircleDashed,
  Banknote,
  Download,
  Trash2,
} from "lucide-react";
import { cn } from "@/lib/utils";

const statusConfig = {
  paid: {
    icon: CheckCircle2,
    iconColor: "text-emerald-500",
    label: "Paid",
    bg: "bg-emerald-50",
    border: "border-emerald-100",
    labelColor: "text-emerald-700",
  },
  pending: {
    icon: Clock,
    iconColor: "text-blue-400",
    label: "Pending",
    bg: "bg-blue-50",
    border: "border-blue-100",
    labelColor: "text-blue-600",
  },
  missed: {
    icon: AlertCircle,
    iconColor: "text-red-500",
    label: "Missed",
    bg: "bg-red-50",
    border: "border-red-100",
    labelColor: "text-red-600",
  },
  partial: {
    icon: CircleDashed,
    iconColor: "text-amber-500",
    label: "Partial",
    bg: "bg-amber-50",
    border: "border-amber-100",
    labelColor: "text-amber-600",
  },
};

export function InstalmentSchedule({
  plan,
  onRecordPayment,
  onDownloadReceipt,
  onClear,
}) {
  if (!plan || !plan.instalments?.length) return null;

  const totalKnown = typeof plan.totalInstalments === "number";
  const paidCount = plan.instalments.filter(
    (i) => i.status === "paid" || i.status === "partial",
  ).length;
  const missedCount = plan.instalments.filter(
    (i) => i.status === "missed",
  ).length;
  const canClear = paidCount === 0;

  // Index of next upcoming pending instalment
  const nextIdx = plan.instalments.findIndex(
    (i) => i.status === "pending" && new Date(i.expectedDate) > new Date(),
  );

  const actionableInstalments = plan.instalments.filter(
    (i) => i.status === "pending" || i.status === "missed",
  );
  const settleableTotal = actionableInstalments.reduce(
    (sum, i) => sum + (i.expectedAmount || 0),
    0,
  );

  return (
    <div className="rounded-2xl border border-border/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-muted/30 border-b border-border/40">
        <div>
          <p className="text-xs font-semibold text-foreground">
            Instalment Plan
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            {totalKnown
              ? `${plan.totalInstalments} instalments · ${plan.frequency?.replace("_", "-")}`
              : `Open-ended · ${plan.frequency?.replace("_", "-")}`}
          </p>
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          <span className="text-emerald-600 font-semibold">
            {paidCount} paid
          </span>
          {missedCount > 0 && (
            <span className="text-red-600 font-semibold">
              {missedCount} missed
            </span>
          )}
          {actionableInstalments.length > 1 && settleableTotal > 0 && (
            <button
              onClick={() =>
                onRecordPayment?.({
                  suggestedAmount: settleableTotal,
                  label: `All ${actionableInstalments.length} Remaining Instalments`,
                })
              }
              className="flex items-center gap-1 text-[9px] font-semibold px-2 py-1 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
            >
              <Banknote className="h-2.5 w-2.5" />
              Pay All
            </button>
          )}
          {canClear && onClear && (
            <button
              onClick={onClear}
              title="Clear stale plan"
              className="flex items-center gap-1 text-[9px] font-semibold px-2 py-1 rounded-full bg-red-50 text-red-600 hover:bg-red-100 transition-colors border border-red-200"
            >
              <Trash2 className="h-2.5 w-2.5" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Instalment rows */}
      <div className="divide-y divide-border/30">
        {plan.instalments.map((inst, idx) => {
          const cfg = statusConfig[inst.status] || statusConfig.pending;
          const Icon = cfg.icon;
          const isNext = idx === nextIdx;

          return (
            <div
              key={inst.instalmentNumber}
              className={cn(
                "flex items-center gap-3 px-4 py-3",
                isNext && "bg-blue-50/40",
              )}
            >
              {/* Icon */}
              <Icon className={cn("h-4 w-4 shrink-0", cfg.iconColor)} />

              {/* Number + date */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-xs font-semibold">
                    Instalment {inst.instalmentNumber}
                  </p>
                  {isNext && (
                    <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600">
                      NEXT
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {inst.status === "paid" || inst.status === "partial"
                    ? `Paid on ${new Date(inst.paidDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`
                    : `Due ${new Date(inst.expectedDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`}
                </p>
              </div>

              {/* Amount */}
              <div className="text-right shrink-0">
                {inst.status === "paid" || inst.status === "partial" ? (
                  <>
                    <p className="text-xs font-bold tabular-nums text-emerald-600">
                      ₹{inst.paidAmount?.toLocaleString()}
                    </p>
                    {inst.status === "partial" && inst.expectedAmount && (
                      <p className="text-[9px] text-muted-foreground">
                        of ₹{inst.expectedAmount?.toLocaleString()}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-xs tabular-nums text-muted-foreground">
                    {inst.expectedAmount
                      ? `₹${inst.expectedAmount?.toLocaleString()}`
                      : "—"}
                  </p>
                )}
              </div>

              {/* Status badge */}
              <span
                className={cn(
                  "text-[9px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 border",
                  cfg.bg,
                  cfg.border,
                  cfg.labelColor,
                )}
              >
                {cfg.label}
              </span>

              {/* Action buttons */}
              {(inst.status === "pending" ||
                inst.status === "missed" ||
                inst.status === "partial") && (
                <button
                  onClick={() =>
                    onRecordPayment?.({
                      suggestedAmount: inst.expectedAmount || null,
                      label: `Instalment ${inst.instalmentNumber}`,
                    })
                  }
                  className="flex items-center gap-1 text-[9px] font-semibold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors border border-emerald-200 shrink-0"
                >
                  <Banknote className="h-2.5 w-2.5" />
                  Pay
                </button>
              )}
              {inst.status === "paid" && inst.paymentId && (
                <button
                  onClick={() => onDownloadReceipt?.(inst.paymentId)}
                  className="flex items-center gap-1 text-[9px] font-semibold px-2 py-1 rounded-full bg-muted text-muted-foreground hover:bg-muted/80 transition-colors border border-border/50 shrink-0"
                >
                  <Download className="h-2.5 w-2.5" />
                  Receipt
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
